import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";

import type { Counterparty } from "../entities/counterparties";
import { validateEmail } from "../entities/counterparties";
import { ContactFields } from "./CounterpartyFields";
import {
  contactInput,
  emptyContactDraft,
  type ContactDraft,
} from "./contactDraft";
import { CounterpartyForm } from "./CounterpartyForm";
import {
  requestErrorText,
  serverFieldErrors,
  useSubmissionKey,
} from "./counterpartiesSupport";
import { SenderRulesPanel } from "./SenderRulesPanel";
import type { ShellServices } from "./services";
import { counterpartyKeys } from "./useCounterpartyNames";
import { Notice } from "./Notice";

type CounterpartyService = NonNullable<ShellServices["counterparties"]>;

function AddContactForm({
  service,
  counterparty,
  onAdded,
}: {
  service: CounterpartyService;
  counterparty: Counterparty;
  onAdded: () => void;
}) {
  const key = useSubmissionKey();
  const [draft, setDraft] = useState<ContactDraft>(emptyContactDraft);
  const [submitted, setSubmitted] = useState(false);
  const add = useMutation({
    mutationFn: () =>
      service.addContact(counterparty.id, contactInput(draft), key.current()),
    onSuccess: () => {
      key.reset();
      setDraft(emptyContactDraft);
      setSubmitted(false);
      onAdded();
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    setSubmitted(true);
    if (validateEmail(draft.email) !== null) return;
    add.mutate();
  }

  return (
    <form className="cp-add-contact" onSubmit={submit} noValidate>
      <h3>Добавить контакт</h3>
      {add.isError ? (
        <Notice>
          {requestErrorText(add.error, "Не удалось добавить контакт.")}
        </Notice>
      ) : null}
      <ContactFields
        draft={draft}
        showErrors={submitted}
        serverErrors={serverFieldErrors(add.error)}
        onChange={(next) => {
          key.reset();
          setDraft(next);
        }}
      />
      <button
        className="button button--secondary button--compact"
        disabled={add.isPending}
      >
        {add.isPending ? "Добавляем…" : "Добавить контакт"}
      </button>
    </form>
  );
}

/**
 * Карточка контрагента: реквизиты, домены, контакты и его правила. Контакт в
 * Core можно только добавить или удалить, поэтому «изменить» контакт здесь
 * нет — это не пропущенная кнопка, а отсутствующая операция.
 */
export function CounterpartyCard({
  service,
  id,
  canManage,
  onBack,
}: {
  service: CounterpartyService;
  id: string;
  canManage: boolean;
  onBack: () => void;
}) {
  const client = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [confirmingContact, setConfirmingContact] = useState<string | null>(
    null,
  );
  const detail = useQuery({
    queryKey: counterpartyKeys.detail(id),
    queryFn: () => service.get(id),
  });
  const refresh = async () => {
    await client.invalidateQueries({ queryKey: counterpartyKeys.all });
  };
  const update = useMutation({
    mutationFn: (values: Parameters<CounterpartyService["update"]>[1]) =>
      service.update(id, values),
    onSuccess: async (saved) => {
      client.setQueryData(counterpartyKeys.detail(id), saved);
      setEditing(false);
      await refresh();
    },
  });
  const removeContact = useMutation({
    mutationFn: (contactId: string) => service.deleteContact(contactId),
    onSuccess: async () => {
      setConfirmingContact(null);
      await refresh();
    },
  });

  const counterparty = detail.data;

  return (
    <article className="cp-card" aria-labelledby="cp-card-title">
      <button
        type="button"
        className="button button--text button--compact cp-back"
        onClick={onBack}
      >
        ← К списку
      </button>

      {detail.isLoading ? <p aria-busy="true">Загружаем карточку…</p> : null}
      {detail.isError ? (
        <div className="cp-empty">
          <Notice>
            {requestErrorText(detail.error, "Не удалось загрузить карточку.")}
          </Notice>
          <button
            type="button"
            className="button button--secondary button--compact"
            onClick={() => void detail.refetch()}
          >
            Повторить
          </button>
        </div>
      ) : null}

      {counterparty === undefined ? null : (
        <>
          <header className="cp-card__head">
            <div>
              <h2 id="cp-card-title">{counterparty.name}</h2>
              <p className="muted">
                {counterparty.contacts.length} контакт(ов) ·{" "}
                {counterparty.domains.length} домен(ов)
              </p>
            </div>
            {canManage && !editing ? (
              <button
                type="button"
                className="button button--secondary button--compact"
                onClick={() => setEditing(true)}
              >
                Изменить
              </button>
            ) : null}
          </header>

          {editing ? (
            <CounterpartyForm
              counterparty={counterparty}
              pending={update.isPending}
              error={update.error}
              onCancel={() => {
                update.reset();
                setEditing(false);
              }}
              onSubmit={(values) =>
                update.mutate({
                  name: values.name,
                  inn: values.inn,
                  kpp: values.kpp,
                  notes: values.notes,
                  domains: values.domains,
                })
              }
            />
          ) : (
            <>
              <dl className="cp-requisites">
                <div>
                  <dt>ИНН</dt>
                  <dd>{counterparty.inn ?? "—"}</dd>
                </div>
                <div>
                  <dt>КПП</dt>
                  <dd>{counterparty.kpp ?? "—"}</dd>
                </div>
                <div className="cp-requisites__wide">
                  <dt>Примечание</dt>
                  <dd>{counterparty.notes ?? "—"}</dd>
                </div>
                <div className="cp-requisites__wide">
                  <dt>Email-домены</dt>
                  <dd>
                    {counterparty.domains.length === 0 ? (
                      "Не указаны"
                    ) : (
                      <ul className="cp-chips" aria-label="Домены контрагента">
                        {counterparty.domains.map((domain) => (
                          <li key={domain} className="cp-chip">
                            {domain}
                          </li>
                        ))}
                      </ul>
                    )}
                  </dd>
                </div>
              </dl>
            </>
          )}

          <section className="cp-section" aria-labelledby="cp-contacts-title">
            <h3 id="cp-contacts-title">Контакты</h3>
            {removeContact.isError ? (
              <Notice>
                {requestErrorText(
                  removeContact.error,
                  "Не удалось удалить контакт.",
                )}
              </Notice>
            ) : null}
            {counterparty.contacts.length === 0 ? (
              <p className="cp-empty">
                Контактов пока нет. Email контакта помогает определять
                отправителя писем и подставляется в поле «Кому» во внешней
                переписке.
              </p>
            ) : (
              <ul className="cp-contact-list">
                {counterparty.contacts.map((contact) => (
                  <li key={contact.id}>
                    <span>
                      <strong>{contact.email}</strong>
                      {contact.is_primary ? (
                        <span className="status-badge status-badge--active">
                          Основной
                        </span>
                      ) : null}
                      <span className="muted">
                        {[contact.name, contact.position]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </span>
                    {canManage ? (
                      confirmingContact === contact.id ? (
                        <span
                          className="cp-confirm"
                          role="group"
                          aria-label="Подтверждение удаления"
                        >
                          <button
                            type="button"
                            className="button button--danger button--compact"
                            disabled={removeContact.isPending}
                            onClick={() => removeContact.mutate(contact.id)}
                          >
                            Удалить контакт
                          </button>
                          <button
                            type="button"
                            className="button button--text button--compact"
                            onClick={() => setConfirmingContact(null)}
                          >
                            Отмена
                          </button>
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="button button--text button--compact"
                          aria-label={`Удалить контакт ${contact.email}`}
                          onClick={() => setConfirmingContact(contact.id)}
                        >
                          Удалить
                        </button>
                      )
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
            {canManage ? (
              <AddContactForm
                key={counterparty.id}
                service={service}
                counterparty={counterparty}
                onAdded={() => void refresh()}
              />
            ) : null}
          </section>

          <SenderRulesPanel
            service={service}
            canManage={canManage}
            counterpartyId={counterparty.id}
          />
        </>
      )}
    </article>
  );
}
